import { Geolocation } from "@capacitor/geolocation";
import { useCallback, useEffect, useState } from "react";
import { Coordinates, isNullOrUndefined } from "use-smart-locks-shared";
import { Sentry } from "../utils/sentry";

type GeolocationState = {
  latitude: number | null;
  longitude: number | null;
  error: string | null;
  isLoading: boolean;
};

type UseGeolocationReturn = GeolocationState & {
  updateLocation: () => Promise<GeolocationState>;
  getLocationOrThrowError: () => Promise<Coordinates>;
};

type UseWatchGeolocationReturn = GeolocationState & {
  retry: () => void;
};

/**
 * https://capacitorjs.com/docs/apis/geolocation#errors
 */
enum GeolocationErrorCode {
  // There was en error trying to obtain the location.
  ErrorWhileObtainingLocation = "OS-PLUG-GLOC-0002",
  // Location permission request was denied.
  PermissionDenied = "OS-PLUG-GLOC-0003",
  // Location services are not enabled.
  LocationServicesDisabled = "OS-PLUG-GLOC-0007",
}

const DEFAULT_GEO_ERROR_MESSAGE =
  "Standort konnte nicht ermittelt werden. Bitte versuchen Sie es erneut. Wenn das Problem weiterhin besteht, kontaktieren Sie bitte den Support.";

function parseGeolocationError(error: unknown): string {
  if (error instanceof Error) {
    const code = "code" in error ? error.code : "";

    switch (code) {
      case GeolocationErrorCode.LocationServicesDisabled:
        return "Bitte aktivieren Sie die Standortdienste auf Ihrem Gerät und versuchen Sie es erneut.";
      case GeolocationErrorCode.PermissionDenied:
        return "Standortabfrage wurde abgelehnt. Bitte geben Sie der App die Berechtigungen, um den genauen Standort abzurufen. Versuchen Sie es danach erneut.";
      case GeolocationErrorCode.ErrorWhileObtainingLocation:
      default:
        return DEFAULT_GEO_ERROR_MESSAGE;
    }
  }
  return DEFAULT_GEO_ERROR_MESSAGE;
}

export function useGeolocation(): UseGeolocationReturn {
  const [state, setState] = useState<GeolocationState>({
    latitude: null,
    longitude: null,
    error: null,
    isLoading: false,
  });

  const updateLocation = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 20_000,
      });
      const newState = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        error: null,
        isLoading: false,
      };
      setState(newState);
      return newState;
    } catch (error) {
      const errorMessage = parseGeolocationError(error);

      Sentry.captureException(error, {
        extra: {
          errorMessage,
        },
      });

      const newState = {
        latitude: null,
        longitude: null,
        error: errorMessage,
        isLoading: false,
      };
      setState(newState);
      return newState;
    }
  }, []);

  const getLocationOrThrowError =
    useCallback(async (): Promise<Coordinates> => {
      const result = await updateLocation();
      if (result.error) {
        throw new Error(result.error);
      }
      if (
        isNullOrUndefined(result.latitude) ||
        isNullOrUndefined(result.longitude)
      ) {
        throw new Error(
          "Standort konnte nicht ermittelt werden. Bitte versuchen Sie es erneut.",
        );
      }
      return { latitude: result.latitude, longitude: result.longitude };
    }, [updateLocation]);

  return {
    ...state,
    updateLocation,
    getLocationOrThrowError,
  };
}

/**
 * Continuously tracks the user's position via Capacitor's watchPosition.
 * Use this when you need live location updates (e.g. a map dot that follows the user).
 */
export function useWatchGeolocation(): UseWatchGeolocationReturn {
  const [state, setState] = useState<GeolocationState>({
    latitude: null,
    longitude: null,
    error: null,
    isLoading: true,
  });
  const [watchEpoch, setWatchEpoch] = useState(0);

  const retry = useCallback(() => {
    setWatchEpoch((n) => n + 1);
  }, []);

  useEffect(() => {
    setState({ latitude: null, longitude: null, error: null, isLoading: true });

    let watchId: string | null = null;
    let cancelled = false;

    Geolocation.watchPosition(
      { enableHighAccuracy: true, timeout: 10_000 },
      (position, err) => {
        if (cancelled) return;

        if (err || !position) {
          const error: unknown =
            err ??
            new Error("Geolocation watch: position missing without error");
          const errorMessage = parseGeolocationError(error);
          Sentry.captureException(error, { extra: { errorMessage } });
          setState({
            latitude: null,
            longitude: null,
            error: errorMessage,
            isLoading: false,
          });
          return;
        }

        setState({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          error: null,
          isLoading: false,
        });
      },
    )
      .then((id) => {
        if (cancelled) {
          void Geolocation.clearWatch({ id });
        } else {
          watchId = id;
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const errorMessage = parseGeolocationError(err);
        Sentry.captureException(err, { extra: { errorMessage } });
        setState({
          latitude: null,
          longitude: null,
          error: errorMessage,
          isLoading: false,
        });
      });

    return () => {
      cancelled = true;
      if (watchId !== null) {
        void Geolocation.clearWatch({ id: watchId });
      }
    };
  }, [watchEpoch]);

  return { ...state, retry };
}
